import React, { useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Typography,
  Paper,
  Fade,
  Grid,
  Stack,
  Avatar,
  Card,
  CardContent,
  Toolbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  CalendarToday,
  Warning as WarningIcon,
  CheckCircle,
  Cancel,
  Description,
  ExpandMore,
  ExpandLess,
  Download,
} from '@mui/icons-material';
import HomeHeader from '../../../../Components/HomeHeader';
import HomeFooter from '../../../../Components/HomeFooter';
import blitzBolt from '../../../../Components/globalAssets/blitzBoltBlack.png';
import { professionalTheme } from './themes';
import ApprovalStamp from './approvalstamp';
import InvoiceActions from './invoiceactions';
import BankingInfo from './bankinginfo';

const ProjectInvoiceDesign = ({ 
  invoice, 
  user, 
  company,
  companyInfo,
  invoiceSettings,
  payouts, 
  onDownloadPDF,
  onCopyInvoiceNumber,
  pdfGenerating, 
  formatDate,
  formatDateTime, 
  getStatusColor, 
  getStatusText,
  getEffectiveValue,
  getInvoiceNotes,
  getInvoiceTerms,
  daysLate,
  totalDaysToPay,
  calculatedDueDate,
  supportingDocument,
  needsApproval,
  userInfo,
  onOpenApprovalDialog,
  onOpenLoginDialog,
  billedToEmail,
  billedToName,
  isAuthenticated = false,
  projectInfo,  // NEW: Enhanced project data
  clientInfo,   // NEW: Enhanced client data
  creatorNames = [],
  lineItems = [],
}) => {
  const [showSupportingDoc, setShowSupportingDoc] = useState(false);
  
  const safeCreatorNames = Array.isArray(creatorNames) ? creatorNames.filter(Boolean) : [];
  const safeLineItems = Array.isArray(lineItems) ? lineItems.filter(item => item != null) : [];
  const lineItemCreatorNames = safeLineItems
    .map((item) => item?.name)
    .filter(Boolean);
  
  const safeAmount = (amt) => (typeof amt === 'number' ? amt : 0);
  const amountDue = safeAmount(invoice?.amount_due);
  const lineItemsSubtotal = safeLineItems.length > 0
    ? safeLineItems.reduce((acc, item) => acc + safeAmount(item?.amount), 0)
    : 0;
  const subtotal = safeLineItems.length > 0
    ? lineItemsSubtotal
    : invoice?.invoice_items?.length > 0
    ? invoice.invoice_items.reduce((acc, item) => acc + safeAmount(item?.amount), 0)
    : amountDue;
  const platformFee = safeAmount(invoice?.platform_fee);
  const feePercentage = invoice?.fee_percentage || 0;
  const outstanding = amountDue;
  const creatorNamesForDisplay = safeCreatorNames.length > 0 ? safeCreatorNames : lineItemCreatorNames;
  const creatorSummary =
    creatorNamesForDisplay.length === 0
      ? ''
      : creatorNamesForDisplay.length <= 3
        ? creatorNamesForDisplay.join(', ')
        : `${creatorNamesForDisplay.slice(0, 3).join(', ')} + ${creatorNamesForDisplay.length - 3} more`;

  const effectiveInvoiceNumber = getEffectiveValue('invoice_number', invoice?.po_number);
  const effectiveDateIssued = getEffectiveValue('date_of_issue', invoice?.created_at);
  const customDetails = invoice?.custom_details || {};
  const billingAddress = customDetails?.billing_address || {};
  const billingAddressParts = [
    billingAddress.address_line1,
    billingAddress.address_line2,
    [billingAddress.city, billingAddress.state].filter(Boolean).join(', '),
    billingAddress.zip_code,
    billingAddress.country,
  ].filter(Boolean);
  const effectiveBillingAddress = billingAddressParts.join(', ');
  const hasBillingAddressOverride = Boolean(
    billingAddress.company_name || effectiveBillingAddress
  );
  
  // Use enhanced client info with priority system
  const effectiveBilledTo = clientInfo?.full_name || clientInfo?.company || 
                            invoice?.client_name || 
                            (user ? `${user.first_name || ''} ${user.last_name || ''}` : 'N/A');
  const effectiveBilledToEmail = clientInfo?.email || invoice?.client_email || user?.email || 'N/A';
  const effectiveBilledToPhone = clientInfo?.phone_number || 'N/A';
  const effectiveBilledToCompany = billingAddress.company_name || clientInfo?.company || invoice?.client_name || 'N/A';
  
  const paymentDate = invoice?.payment_date;
  const paymentTerm = invoice?.payment_term;
  const invoiceStatus = invoice?.status || invoice?.invoice_status;
  
  const invoiceNotes = getInvoiceNotes();
  const invoiceTerms = getInvoiceTerms();

  // Project specific fields - now from enhanced data
  const projectName =
    projectInfo?.name ||
    invoice?.campaign_name ||
    invoice?.project_name ||
    invoice?.project_title ||
    safeLineItems.find((item) => item?.name)?.name ||
    '';
  const invoiceBankOverride = {
    ...(customDetails?.billed_to_info?.bank_details || {}),
    ...(customDetails?.additional_info?.bank_details || {}),
  };
  // Override company info to always be Blitz Systems Corp for project invoices
  const blitzCompanyInfo = {
    name: "Blitz Systems Corp",
    logo: companyInfo?.logo || null,
    address_line_1: "8724 Cavell Lane",
    city: "Houston",
    state: "TX",
    postal_code: "77055",
    email: "win@useblitz.co",
    phone_number: companyInfo?.phone_number || null,
    website: "https://www.useblitz.co"
  };
  const projectBankDetails = {
    account_holder: invoiceBankOverride.account_holder || companyInfo?.account_holder || null,
    account_name: invoiceBankOverride.account_name || null,
    bank_name: invoiceBankOverride.bank_name || companyInfo?.bank_name || null,
    bank_address: invoiceBankOverride.bank_address || companyInfo?.bank_address || null,
    account_number: invoiceBankOverride.account_number || companyInfo?.account_number || null,
    routing_number: invoiceBankOverride.routing_number || companyInfo?.routing_number || null,
    swift_code: invoiceBankOverride.swift_code || invoiceBankOverride.swift || companyInfo?.swift_code || null,
    beneficiary_address: invoiceBankOverride.beneficiary_address || companyInfo?.beneficiary_address || null,
  };
  const invoiceMeta = [
    { label: 'Issue Date', value: formatDate(effectiveDateIssued) },
    { label: 'Invoice #', value: effectiveInvoiceNumber || 'N/A' },
    { label: 'Payment Term', value: paymentTerm ? `${paymentTerm} days` : 'N/A' },
    {
      label: paymentDate ? 'Payment Date' : 'Due Date',
      value: paymentDate ? formatDate(paymentDate) : (calculatedDueDate ? formatDate(calculatedDueDate) : formatDate(invoice?.created_at)),
    },
  ];
  const billedToFields = hasBillingAddressOverride
    ? [
        { label: 'Company', value: effectiveBilledToCompany },
        ...(effectiveBillingAddress ? [{ label: 'Address', value: effectiveBillingAddress }] : []),
      ]
    : [
        { label: 'Name', value: effectiveBilledTo },
        { label: 'Company', value: effectiveBilledToCompany },
        { label: 'Email', value: effectiveBilledToEmail },
        ...(effectiveBilledToPhone !== 'N/A' ? [{ label: 'Phone', value: effectiveBilledToPhone }] : []),
        ...(clientInfo?.location ? [{ label: 'Location', value: clientInfo.location }] : []),
      ];
  const INVOICE_RED = '#c63b4a';
  const INVOICE_RED_SOFT = '#fdf1f3';
  const INVOICE_BORDER = '#ead7dc';
  const INVOICE_TEXT_MUTED = '#6b7280';
  return (
    <ThemeProvider theme={professionalTheme}>
      <Box sx={{ 
        minHeight: '100vh', 
        background: '#f7f4f3',
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        <HomeHeader />
        <Toolbar />

        {/* Action Bar */}
        <InvoiceActions
          onDownloadPDF={onDownloadPDF}
          pdfGenerating={pdfGenerating}
          onCopyInvoiceNumber={onCopyInvoiceNumber}
          invoiceNumber={effectiveInvoiceNumber}
          invoiceStatus={invoiceStatus}
          createdDate={effectiveDateIssued}
          getStatusText={getStatusText}
          getStatusColor={getStatusColor}
          formatDate={formatDate}
          variant="project"
          isAuthenticated={isAuthenticated}
        />

        <Box sx={{ py: 2, flex: 1, maxWidth: '100vw', overflowX: 'hidden' }}>
          {/* Enhanced Approval Banner with Approver Details */}
          {needsApproval && (
            <Fade in={true} timeout={600}>
              <Box sx={{ width: { xs: '95%', sm: '210mm' }, maxWidth: '100%', m: '0 auto', mb: 2 }}>
                <Alert 
                  severity="warning" 
                  sx={{ 
                    borderRadius: 3,
                    '& .MuiAlert-message': { width: '100%' }
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="h6" fontWeight={600}>
                      This project invoice requires approval
                    </Typography>
                    
                    <Typography variant="body2">
                      <strong>Action Required:</strong> This invoice needs to be reviewed and approved by the billed party:
                    </Typography>
                    
                    {/* Highlighted Approver Details Box */}
                    <Box sx={{ 
                      pl: 2, 
                      py: 1.5, 
                      borderLeft: '3px solid',
                      borderColor: 'warning.main',
                      bgcolor: 'rgba(237, 108, 2, 0.08)',
                      borderRadius: 1
                    }}>
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        Approver Details:
                      </Typography>
                      <Typography variant="body2">
                        Name: <strong>{billedToName || 'Not specified'}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Email: <strong>{billedToEmail || 'Not specified'}</strong>
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      If you are {billedToName || 'the billed party'}, please review the invoice details below and approve or decline.
                    </Typography>
                    
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      {userInfo ? (
                        <>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircle />}
                            onClick={onOpenApprovalDialog}
                            sx={{ borderRadius: 2 }}
                          >
                            Approve Invoice
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={onOpenApprovalDialog}
                            sx={{ borderRadius: 2 }}
                          >
                            Decline Invoice
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={onOpenLoginDialog}
                          sx={{ borderRadius: 2 }}
                        >
                          Login to Approve/Decline
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Alert>
              </Box>
            </Fade>
          )}

          <Paper
            id="invoice-content"
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              width: { xs: '95%', sm: '210mm' },
              maxWidth: '100%',
              minHeight: { xs: 'auto', sm: '297mm' },
              m: '0 auto',
              background: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 6,
                background: INVOICE_RED,
              }
            }}
          >
            {/* Header Section */}
            <Box sx={{ mt: 1.25, mb: 3 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${INVOICE_BORDER}`,
                  background: '#ffffff',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={7}>
                      <Stack spacing={1.1}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              letterSpacing: '0.12em',
                              textTransform: 'uppercase',
                              color: INVOICE_RED,
                              mb: 0.35,
                            }}
                          >
                            Project Invoice
                          </Typography>
                          <Box
                            sx={{
                              fontSize: { xs: '1.9rem', sm: '2.15rem' },
                              fontWeight: 800,
                              color: '#0f172a',
                              lineHeight: 0.98,
                              letterSpacing: '-0.04em',
                            }}
                          >
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
                              <Box component="span">BLIT</Box>
                              <Box
                                component="img"
                                src={blitzBolt}
                                alt="Z"
                                sx={{
                                  width: { xs: 35, sm: 40 },
                                  height: { xs: 35, sm: 40 },
                                  objectFit: 'contain',
                                  mx: '-1px',
                                  transform: 'translate(-6px, 7px)',
                                }}
                              />
                            </Box>
                            <Box component="span" sx={{ mr : 17}}>
                              COLLABS
                            </Box>
                            <br />
                            INVOICE
                          </Box>
                        </Box>

                        <Box>
                          <Typography sx={{ color: INVOICE_TEXT_MUTED, fontWeight: 500, fontSize: '0.96rem', mb: 0.45 }}>
                            From {blitzCompanyInfo.name}
                          </Typography>
                          {projectName && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: '#1e293b',
                                lineHeight: 1.35,
                                wordBreak: 'break-word',
                              }}
                            >
                              Project: {projectName}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <Card
                        sx={{
                          borderRadius: 2.75,
                          background: '#ffffff',
                          border: `1px solid ${INVOICE_BORDER}`,
                          boxShadow: 'none',
                          overflow: 'visible',
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Stack spacing={1.1}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.85 }}>
                              <Chip
                                label={getStatusText(invoiceStatus)}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  maxWidth: '100%',
                                  backgroundColor: INVOICE_RED,
                                  color: '#ffffff',
                                }}
                              />
                              <Box>
                                <Typography variant="body2" sx={{ mb: 0.35, color: INVOICE_TEXT_MUTED }}>
                                  Total Amount Due
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    color: '#0f172a',
                                    fontSize: { xs: '2rem', sm: '2.45rem' },
                                    lineHeight: 1.02,
                                    letterSpacing: '-0.04em',
                                  }}
                                >
                                  ${outstanding.toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>

            {/* Approval Stamp */}
            {invoice?.approval_proof && (
              <ApprovalStamp 
                approvalInfo={invoice.approval_proof}
                invoice={invoice}
              />
            )}

            {/* Embedded Supporting Document Section */}
            {supportingDocument && (
              <Card 
                variant="outlined" 
                data-supporting-doc-section
                sx={{ 
                  mb: 3.5, 
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: INVOICE_BORDER,
                  backgroundColor: '#ffffff',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack 
                    direction="row" 
                    justifyContent="space-between" 
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setShowSupportingDoc(!showSupportingDoc)}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: INVOICE_RED_SOFT, color: INVOICE_RED }}>
                        <Description />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600} sx={{ color: '#111827' }}>
                          Supporting Documents Attached
                        </Typography>
                        <Typography variant="body2" sx={{ color: INVOICE_TEXT_MUTED }}>
                          Click to {showSupportingDoc ? 'hide' : 'view'} attached documents
                        </Typography>
                      </Box>
                    </Stack>
                    <IconButton>
                      {showSupportingDoc ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                  
                  <Collapse in={showSupportingDoc}>
                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${INVOICE_BORDER}` }}>
                      <iframe
                        src={supportingDocument}
                        style={{ 
                          width: '100%', 
                          height: '500px', 
                          border: `1px solid ${INVOICE_BORDER}`,
                          borderRadius: '8px'
                        }}
                        title="Supporting Document PDF"
                      />
                      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<Download />}
                          href={supportingDocument}
                          download
                          target="_blank"
                        >
                          Download Document
                        </Button>
                      </Stack>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            )}

            {/* Invoice Details */}
            <Card sx={{ mb: 3, borderRadius: 2.5, border: `1px solid ${INVOICE_BORDER}`, bgcolor: '#ffffff', boxShadow: 'none' }}>
              <CardContent sx={{ p: 1.4 }}>
                <Grid container spacing={1.25}>
                  {invoiceMeta.map((item) => (
                    <Grid item xs={6} md={3} key={item.label}>
                      <Stack spacing={0.2}>
                        <Typography variant="caption" sx={{ lineHeight: 1.1, color: INVOICE_RED }}>
                          {item.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.88rem',
                            lineHeight: 1.2,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Payment Timeline Info */}
            {(totalDaysToPay !== 'N/A' || (daysLate !== 'N/A' && daysLate > 0)) && (
              <Card sx={{ mb: 3, borderRadius: 3, bgcolor: INVOICE_RED_SOFT, border: `1px solid ${INVOICE_BORDER}`, boxShadow: 'none' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ color: INVOICE_RED }}>
                    Payment Timeline
                  </Typography>
                  <Grid container spacing={1.5}>
                    {totalDaysToPay !== 'N/A' && (
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CalendarToday sx={{ color: INVOICE_RED }} />
                          <Typography variant="body2">Total Days to Pay:</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {totalDaysToPay} days
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {daysLate !== 'N/A' && daysLate > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <WarningIcon sx={{ color: 'error.main' }} />
                          <Typography variant="body2" color="error">Days Late:</Typography>
                          <Typography variant="body2" fontWeight={600} color="error">
                            {daysLate} days
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}


            {/* Billing Information */}
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3, border: `1px solid ${INVOICE_BORDER}`, boxShadow: 'none' }}>
                  <CardContent sx={{ p: 1.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.1 }}>
                      Billed To
                    </Typography>
                    <Grid container spacing={1.25}>
                      {billedToFields.map((field) => (
                        <Grid
                          item
                          xs={12}
                          md={hasBillingAddressOverride && field.label === 'Address' ? 8 : 4}
                          key={field.label}
                        >
                          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, mb: 0.25, color: INVOICE_RED }}>
                            {field.label}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.84rem', lineHeight: 1.25, overflowWrap: 'anywhere' }}>
                            {field.value}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Items Table */}
            <Card sx={{ mb: 3.5, borderRadius: 3, overflow: 'hidden', border: `1px solid ${INVOICE_BORDER}`, boxShadow: 'none' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ background: '#ffffff' }}>
                    <TableRow>
                      <TableCell sx={{ color: INVOICE_RED, fontWeight: 600, py: 1.2, fontSize: '0.82rem', borderTop: `3px solid ${INVOICE_RED}`, borderBottom: `1px solid ${INVOICE_BORDER}` }}>Description</TableCell>
                      <TableCell sx={{ color: INVOICE_RED, fontWeight: 600, py: 1.2, textAlign: 'right', fontSize: '0.82rem', borderTop: `3px solid ${INVOICE_RED}`, borderBottom: `1px solid ${INVOICE_BORDER}` }}>Rate</TableCell>
                      <TableCell sx={{ color: INVOICE_RED, fontWeight: 600, py: 1.2, textAlign: 'center', fontSize: '0.82rem', borderTop: `3px solid ${INVOICE_RED}`, borderBottom: `1px solid ${INVOICE_BORDER}` }}>Qty</TableCell>
                      <TableCell sx={{ color: INVOICE_RED, fontWeight: 600, py: 1.2, textAlign: 'right', fontSize: '0.82rem', borderTop: `3px solid ${INVOICE_RED}`, borderBottom: `1px solid ${INVOICE_BORDER}` }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safeLineItems.length > 0
                      ? safeLineItems.map((item, idx) => (
                          <TableRow key={`line-item-${idx}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f8fafc' } }}>
                            <TableCell sx={{ py: 1.2, whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                              {item?.name || item?.description || 'Creative Services'}
                            </TableCell>
                            <TableCell sx={{ py: 1.2, textAlign: 'right', fontWeight: 500, fontSize: '0.9rem' }}>
                              ${safeAmount(item?.amount).toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 1.2, textAlign: 'center', fontSize: '0.9rem' }}>1</TableCell>
                            <TableCell sx={{ py: 1.2, textAlign: 'right', fontWeight: 600, fontSize: '0.9rem' }}>
                              ${safeAmount(item?.amount).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))
                      : invoice?.invoice_items?.length > 0
                          ? invoice.invoice_items.map((it, idx) => (
                              <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f8fafc' } }}>
                                <TableCell sx={{ py: 1.2, whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{it?.description || 'Creative Services'}</TableCell>
                                <TableCell sx={{ py: 1.2, textAlign: 'right', fontWeight: 500, fontSize: '0.9rem' }}>${(it?.amount || 0).toFixed(2)}</TableCell>
                                <TableCell sx={{ py: 1.2, textAlign: 'center', fontSize: '0.9rem' }}>1</TableCell>
                                <TableCell sx={{ py: 1.2, textAlign: 'right', fontWeight: 600, fontSize: '0.9rem' }}>${(it?.amount || 0).toFixed(2)}</TableCell>
                              </TableRow>
                            ))
                          : creatorNamesForDisplay.length > 0
                            ? (
                              <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                <TableCell sx={{ py: 1.2, whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                                  {creatorSummary || 'Creative Services'}
                                </TableCell>
                                <TableCell sx={{ py: 1.2, textAlign: 'right', fontWeight: 500, fontSize: '0.9rem' }}>
                                  ${amountDue.toFixed(2)}
                                </TableCell>
                                <TableCell sx={{ py: 1.2, textAlign: 'center', fontSize: '0.9rem' }}>1</TableCell>
                                <TableCell sx={{ py: 1.2, textAlign: 'right', fontWeight: 600, fontSize: '0.9rem' }}>
                                  ${amountDue.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            )
                          : (
                            <TableRow>
                              <TableCell>Creative Services</TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 500 }}>${amountDue.toFixed(2)}</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>1</TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>${amountDue.toFixed(2)}</TableCell>
                            </TableRow>
                          )}
                    <TableRow sx={{ '& td': { borderBottom: 'none' } }}>
                      <TableCell colSpan={2} />
                      <TableCell
                        sx={{
                          py: 0.9,
                          textAlign: 'right',
                          pr: 5,
                          fontSize: '0.84rem',
                          color: INVOICE_RED,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Subtotal
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 0.9,
                          textAlign: 'right',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ${(subtotal || amountDue).toFixed(2)}
                      </TableCell>
                    </TableRow>
                    {platformFee > 0 && (
                      <TableRow sx={{ '& td': { borderBottom: 'none' } }}>
                        <TableCell colSpan={2} />
                        <TableCell
                          sx={{
                            py: 0.9,
                            textAlign: 'right',
                            pr: 7,
                            fontSize: '0.84rem',
                            color: INVOICE_RED,
                          }}
                        >
                          Platform Fee {feePercentage > 0 && `(${feePercentage}%)`}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 0.9,
                            textAlign: 'right',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ${platformFee.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow
                      sx={{
                        '& td': {
                          borderTop: `1px solid ${INVOICE_BORDER}`,
                          borderBottom: 'none',
                        },
                      }}
                    >
                      <TableCell colSpan={2} />
                      <TableCell
                        sx={{
                          py: 1.1,
                          textAlign: 'right',
                          pr: 4,
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Total Due
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.1,
                          textAlign: 'right',
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ${outstanding.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            {/* Notes */}
            {invoiceNotes && (
              <Box sx={{ mb: 3 }}>
                <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#ffffff', borderColor: INVOICE_BORDER }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: INVOICE_RED }}>
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.88rem' }}>
                      {invoiceNotes}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Payment Terms */}
            {invoiceTerms && (
              <Box sx={{ mb: 3 }}>
                <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#ffffff', borderColor: INVOICE_BORDER }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: INVOICE_RED }}>
                      Payment Terms & Conditions
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.88rem' }}>
                      {invoiceTerms}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Banking Information */}
            <BankingInfo companyInfo={blitzCompanyInfo} bankDetails={projectBankDetails} variant="project" />

            {/* Footer */}
            <Box sx={{ mt: 3, pt: 2, borderTop: `2px solid ${INVOICE_BORDER}`, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.82rem' }}>
                Questions about this invoice? Contact us at {blitzCompanyInfo.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.74rem' }}>
                {blitzCompanyInfo.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.74rem' }}>
                {blitzCompanyInfo.address_line_1}
                {blitzCompanyInfo.city && `, ${blitzCompanyInfo.city}`}
                {blitzCompanyInfo.state && `, ${blitzCompanyInfo.state}`}
                {blitzCompanyInfo.postal_code && ` ${blitzCompanyInfo.postal_code}`}
              </Typography>
              {blitzCompanyInfo.phone_number && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.74rem' }}>
                  Phone: {blitzCompanyInfo.phone_number}
                </Typography>
              )}
              {blitzCompanyInfo.website && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.74rem' }}>
                  {blitzCompanyInfo.website}
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>

        <HomeFooter />
      </Box>
    </ThemeProvider>
  );
};

export default ProjectInvoiceDesign;
